import { useEffect, useState } from 'react';
import { iaApi } from '../../api/resources';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FR } from '../../components/ui/FieldRow.jsx';
import { MESSAGE_NON_ACTIVE, messageIa } from '../../lib/ia';
import { C } from '../../theme/palette';
import { tr, trf } from '../../lib/i18n';

/** L'état du pack et de la clé, traduits dans la langue de l'écran. */
const ETAT = {
  souscrite: 'Souscrite',
  nonSouscrite: 'Non souscrite',
  cle: 'Clé configurée',
  sansCle: 'Sans clé',
};

/** Ce que chaque fonction du journal fait, en clair. */
const FONCTIONS = {
  REDACTION: 'Rédaction',
  LECTURE_DEPENSE: 'Lecture de ticket',
  ASSISTANT: 'Assistant',
  LECTURE_SOUMISSION: 'Lecture de soumission',
  LECTURE_CATALOGUE: 'Lecture de catalogue',
  LECTURE_NOTES: 'Offre depuis des notes',
  LECTURE_FACTURE: 'Lecture de facture',
  LECTURE_DOCUMENT: 'Reprise de document',
};

/** Comment un appel a fini, en clair. */
const STATUTS = {
  OK: 'réussi',
  INDISPONIBLE: 'service indisponible',
  REPONSE_INVALIDE: 'réponse inexploitable',
};

/** Date et heure d'un appel, à la suisse. */
const quand = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || '');
  return (
    d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })
  );
};

/**
 * Le Pack IA vu du patron : est-il en service, combien a-t-il servi ce mois.
 *
 * Le pack se facture en sus et chaque appel coûte au fournisseur : le patron
 * doit voir la consommation de sa société sans demander au support. Le journal
 * dit qui a appelé quoi, jamais ce qui a été demandé ni répondu.
 *
 * @param {object} props propriétés du composant
 * @param {(route: string) => void} [props.onNav] navigation vers l'abonnement
 * @returns {import('react').ReactElement} la carte
 */
export function PackIaCard({ onNav }) {
  const [journal, setJournal] = useState(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let annule = false;
    iaApi
      .journal()
      .then((j) => {
        if (!annule) setJournal(j);
      })
      .catch((e) => {
        if (!annule) setErreur(messageIa(e));
      });
    return () => {
      annule = true;
    };
  }, []);

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="✨"
        title="Pack IA"
        color={C.purple}
        sub="Consommation du mois et état du service"
      />
      <div style={{ padding: 13 }}>
        {erreur && (
          <div role="alert" style={{ fontSize: 9.5, color: C.red, marginBottom: 8 }}>
            {erreur}
          </div>
        )}
        {!journal && !erreur && <div style={{ fontSize: 9.5, color: C.muted }}>Chargement…</div>}
        {journal && (
          <>
            <div style={{ background: C.purpleL, borderRadius: 9, padding: 12, marginBottom: 10 }}>
              <FR
                label="Option"
                value={tr(journal.packActif ? ETAT.souscrite : ETAT.nonSouscrite)}
                color={journal.packActif ? C.green : C.red}
                bold
              />
              <FR
                label="Serveur"
                value={
                  journal.configure ? tr('clé configurée') + ' · ' + journal.modele : tr('sans clé')
                }
                color={journal.configure ? C.green : C.red}
              />
              <FR label="Appels ce mois" value={String(journal.moisAppels)} bold />
              <FR
                label="Jetons ce mois"
                value={trf(
                  '{0} lus · {1} produits',
                  journal.moisJetonsEntree,
                  journal.moisJetonsSortie,
                )}
              />
              <FR
                label="Dernière heure"
                value={trf('{0} / {1} appels', journal.appelsHeure, journal.limiteHeure)}
                last
              />
            </div>
            {!journal.configure && (
              <div style={{ fontSize: 9.5, color: C.grey, marginBottom: 10, lineHeight: 1.5 }}>
                {MESSAGE_NON_ACTIVE}
              </div>
            )}
            {!journal.packActif && (
              <div style={{ marginBottom: 10 }}>
                <Btn
                  label="⭐ Souscrire le Pack IA"
                  color={C.purple}
                  full
                  onClick={() => onNav && onNav('abonnement')}
                />
              </div>
            )}
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: C.muted,
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              Derniers appels
            </div>
            {journal.dernieres.length === 0 && (
              <div style={{ fontSize: 9.5, color: C.muted }}>Aucun appel pour l&apos;instant.</div>
            )}
            {journal.dernieres.map((e, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  fontSize: 9.5,
                  padding: '6px 0',
                  borderBottom: '1px solid ' + C.border,
                }}
              >
                <span style={{ color: C.muted, whiteSpace: 'nowrap' }}>{quand(e.debut)}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {FONCTIONS[e.fonction] || e.fonction}
                  {e.utilisateur ? ' · ' + e.utilisateur : ''}
                </span>
                <span style={{ color: e.statut === 'OK' ? C.green : C.red, whiteSpace: 'nowrap' }}>
                  {STATUTS[e.statut] || e.statut}
                  {e.dureeMs != null ? ' · ' + (e.dureeMs / 1000).toFixed(1) + ' s' : ''}
                </span>
              </div>
            ))}
            <div style={{ fontSize: 8.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
              Le journal garde qui a appelé quoi, la durée et les jetons — jamais le contenu des
              demandes ni des réponses. Limite : {journal.limiteHeure} appels par heure pour la
              société.
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
